import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import useNotifications from "../../components/NotificationProvider";
import SidekickUsageService, {
  CsvExportError,
  saveBlob,
} from "../../services/sidekick-usage.service";
import {
  ISidekickPlant,
  ISidekickTurnsResponse,
} from "../../types/api/sidekick-usage.type";
import FiltersPanel from "./FiltersPanel";
import StatsTiles from "./StatsTiles";
import SessionList from "./SessionList";
import { PlantTable, UserTable } from "./SummaryTables";
import { formatDateTime } from "./labels";
import {
  DEFAULT_FILTERS,
  PAGE_SIZES,
  SidekickFilters,
  parseFilters,
  serializeFilters,
  toTurnsQuery,
} from "./urlState";

const cardSx = { backgroundColor: "#2A2B31", mb: 2 };

const errorMessage = (error: any, fallback: string): string => {
  if (typeof error?.error === "string") return `${fallback} (${error.error})`;
  if (typeof error?.message === "string") return `${fallback} (${error.message})`;
  return fallback;
};

export default function SidekickUsage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const { setNotification } = useNotifications();

  const [plants, setPlants] = useState<ISidekickPlant[] | null>(null);
  const [plantsError, setPlantsError] = useState("");
  const [result, setResult] = useState<ISidekickTurnsResponse | null>(null);
  const [resultError, setResultError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const requestSeq = useRef(0);

  const selectablePlantIds = useMemo(
    () => (plants ?? []).filter((p) => p.status !== "suspended").map((p) => p.env_id),
    [plants]
  );
  const plantNames = useMemo(
    () => Object.fromEntries((plants ?? []).map((p) => [p.env_id, p.name])) as Record<number, string>,
    [plants]
  );

  const applyFilters = useCallback(
    (next: SidekickFilters, options?: { replace?: boolean }) => {
      const nextParams = serializeFilters(next);
      if (nextParams.toString() === searchParams.toString()) {
        // Same view requested again: re-run it so "now" moves forward.
        setReloadKey((k) => k + 1);
      } else {
        setSearchParams(nextParams, options);
      }
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    SidekickUsageService.getPlants()
      .then((res) => setPlants(res.data?.plants ?? []))
      .catch((e) => {
        console.error("error", e);
        setPlants([]);
        setPlantsError(errorMessage(e, "Couldn't load the list of plants"));
      });
  }, []);

  // No plants in the URL (first visit, or the menu link back to the bare page):
  // start with every plant that is running.
  const hasEnvIds = searchParams.has("env_ids");
  useEffect(() => {
    if (plants && !hasEnvIds && selectablePlantIds.length) {
      applyFilters({ ...filters, envIds: selectablePlantIds }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plants, hasEnvIds]);

  useEffect(() => {
    if (!filters.envIds.length) {
      setResult(null);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setResultError("");
    SidekickUsageService.getTurns(toTurnsQuery(filters))
      .then((res) => {
        if (seq === requestSeq.current) setResult(res.data);
      })
      .catch((e) => {
        if (seq !== requestSeq.current) return;
        console.error("error", e);
        setResult(null);
        setResultError(errorMessage(e, "Couldn't load Sidekick usage"));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), reloadKey]);

  const handleExport = async () => {
    setExporting(true);
    setExportError("");
    try {
      const query = toTurnsQuery(filters);
      const blob = await SidekickUsageService.exportTurnsCsv(query);
      saveBlob(blob, `sidekick-usage_${query.from.slice(0, 10)}_${query.to.slice(0, 10)}.csv`);
    } catch (e) {
      const message =
        e instanceof CsvExportError ? e.message : "Export failed. Please try again.";
      setExportError(message);
      setNotification({ open: true, type: "error", message });
    } finally {
      setExporting(false);
    }
  };

  const unreachable = result?.unreachable ?? [];
  const pageCount = result ? Math.max(1, Math.ceil(result.total_sessions / result.page_size)) : 1;

  // A stale or shared link can ask for a page past the end: move to the last real page.
  useEffect(() => {
    if (result && !loading && filters.page > pageCount) {
      applyFilters({ ...filters, page: pageCount }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading, pageCount, filters.page]);

  return (
    <>
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ marginBottom: "20px" }}>
        <Typography color="#777">Sidekick usage</Typography>
      </Breadcrumbs>

      <Card sx={cardSx}>
        <CardHeader
          title="Sidekick usage"
          subheader="Questions people asked Sidekick at each plant, read live from the plants. Times are in your local time zone."
          action={
            <Button
              sx={{ marginRight: "10px" }}
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={!filters.envIds.length || exporting}
              onClick={handleExport}
            >
              {exporting ? "Preparing CSV…" : "Export CSV"}
            </Button>
          }
        />
        <CardContent>
          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError("")}>
              {exportError}
            </Alert>
          )}
          {plantsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {plantsError}
            </Alert>
          )}
          {plants && !plantsError && plants.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You don't have access to any plants on this cluster.
            </Alert>
          )}
          <FiltersPanel
            plants={plants ?? []}
            knownUsers={(result?.by_user ?? []).map((u) => u.email)}
            value={filters}
            onApply={(next) => applyFilters(next)}
            resetValue={{ ...DEFAULT_FILTERS, envIds: selectablePlantIds }}
          />
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {resultError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {resultError}
        </Alert>
      )}
      {!filters.envIds.length && plants && plants.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose one or more plants, then click Show results.
        </Alert>
      )}

      {result && (
        <>
          {unreachable.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="unreachable-notice">
              Couldn't reach {unreachable.length === 1 ? "1 plant" : `${unreachable.length} plants`}, so
              these numbers leave them out:{" "}
              {unreachable
                .map((u) => `${plantNames[u.env_id] ?? `#${u.env_id}`} (${u.error})`)
                .join(", ")}
              .
            </Alert>
          )}
          <Typography color="#999" sx={{ mb: 1 }}>
            {formatDateTime(result.window.from)} to {formatDateTime(result.window.to)} ·{" "}
            {result.plants_queried.length === 1
              ? "1 plant"
              : `${result.plants_queried.length} plants`}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <StatsTiles stats={result.stats} />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} xl={5}>
              <Card sx={cardSx}>
                <CardHeader title="By plant" />
                <CardContent>
                  <PlantTable rows={result.by_plant} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} xl={7}>
              <Card sx={cardSx}>
                <CardHeader title="By person" />
                <CardContent>
                  <UserTable
                    rows={result.by_user}
                    plantNames={plantNames}
                    onSelectUser={(email) => applyFilters({ ...filters, users: [email], page: 1 })}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={cardSx}>
            <CardHeader
              title={`Conversations (${result.total_sessions.toLocaleString("en-US")})`}
              subheader="Newest first. Open a conversation to read the full questions and answers."
            />
            <CardContent>
              <SessionList sessions={result.sessions} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
                <Pagination
                  count={pageCount}
                  page={filters.page}
                  onChange={(_, page) => applyFilters({ ...filters, page })}
                  color="primary"
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="sidekick-page-size-label">Conversations per page</InputLabel>
                  <Select
                    labelId="sidekick-page-size-label"
                    label="Conversations per page"
                    value={filters.pageSize}
                    onChange={(e) =>
                      applyFilters({ ...filters, pageSize: Number(e.target.value), page: 1 })
                    }
                  >
                    {PAGE_SIZES.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
